import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../store/store';
import {
  startTour,
  openTour,
  closeTour,
  nextTourStep,
  prevTourStep,
  setTourStep,
  setNavigationComplete,
  forceUpdateTourState,
  startModalTour,
  nextModalStep,
  prevModalStep,
  closeModalTour,
  completeTour,
  resetTour,
  TourStep
} from '../store/slices/tour.slice';
import { PermissionType } from '../types/user.types';
import { all_routes } from '../router/all_routes';

export const useTour = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const tourState = useSelector((state: RootState) => state.tour);
  const { user } = useSelector((state: RootState) => state.auth);

  const shouldShowTour = () => {
    return false; // Artık otomatik gösterilmeyecek, sadece manuel başlatılacak
  };

  const isAdmin = (): boolean => {
    return user?.permissions?.includes(PermissionType.CONTROL_PANEL_ADMIN) || false;
  };

  // 21 adımlık tour için route mapping
  const getStepRoute = (step: TourStep): string => {
    switch (step) {
      case 'dashboard':
        return all_routes.adminDashboard;

      case 'employees':
        return all_routes.employeeList;
      case 'departments':
        return all_routes.departments;
      case 'branches':
        return all_routes.branches;
      case 'attendance-report':
        return all_routes.attendance;
      case 'shifts':
        return all_routes.shift;
      case 'leaves':
        return all_routes.leaveadmin;
      case 'tickets':
        return all_routes.ticketAdmin;
      case 'forgot-checkout':
        return all_routes.forgotCheckOut;
      case 'forgot-checkin':
        return all_routes.forgotCheckIn;
      case 'assets':
        return all_routes.assets;
      case 'meals':
        return all_routes.meals;
      case 'employee-report':
        return all_routes.employeeReport;
      case 'shift-report':
        return all_routes.shiftReport;
      case 'leave-report':
        return all_routes.leaveReport;
      case 'break-report':
        return all_routes.breakReport;
      case 'documents':
        return all_routes.documents;
      case 'timesheet-report':
        return all_routes.timesheetReport;
      case 'company-settings':
        return all_routes.companySettings;
      case 'notifications':
        return all_routes.notification;
      case 'tour-button':
        return all_routes.adminDashboard; // Tour butonu header'da
      case 'completed':
        return all_routes.adminDashboard; // Tour tamamlandı, dashboard'a dön
      default:
        return all_routes.adminDashboard;
    }
  };

  // Tour step adını Türkçe'ye çevir
  const getStepDisplayName = (step: TourStep): string => {
    switch (step) {
      case 'dashboard': return 'Kontrol Paneli';
      case 'employees': return 'Çalışan Listesi';
      case 'departments': return 'Departmanlar';
      case 'branches': return 'Şubeler';
      case 'attendance-report': return 'Giriş Çıkış Raporu';
      case 'shifts': return 'Vardiyalar';
      case 'leaves': return 'İzinler';
      case 'tickets': return 'Talepler';
      case 'forgot-checkout': return 'Çıkış Yapmayanlar';
      case 'forgot-checkin': return 'Giriş Yapmayanlar';
      case 'assets': return 'Zimmet Yönetimi';
      case 'meals': return 'Yemek Yönetimi';
      case 'employee-report': return 'Çalışan Raporu';
      case 'shift-report': return 'Performans Raporu';
      case 'leave-report': return 'İzin Raporu';
      case 'break-report': return 'Mola Raporu';
      case 'documents': return 'Belgeler';
      case 'timesheet-report': return 'Puantaj & Maaş';
      case 'company-settings': return 'Şirket Ayarları';
      case 'notifications': return 'Bildirim';
      case 'tour-button': return 'Tour Butonu';
      case 'completed': return 'Tamamlandı';
      default: return 'Bilinmeyen';
    }
  };

  const startAppTour = () => {
    if (isAdmin()) {
      dispatch(startTour());
      // Dashboard'a git
      const dashboardRoute = getStepRoute('dashboard');
      navigate(dashboardRoute);
    }
  };

  const handleNextTourStep = async () => {

    // 22 adımlık tour steps
    const tourSteps: TourStep[] = [
      'dashboard', 'employees', 'departments', 'branches',
      'attendance-report', 'shifts', 'leaves', 'tickets', 'forgot-checkout',
      'forgot-checkin', 'assets', 'meals', 'employee-report', 'shift-report',
      'leave-report', 'break-report', 'documents', 'timesheet-report',
      'company-settings', 'notifications', 'tour-button', 'completed'
    ];
    
    // Current step'i hesapla
    const currentIndex = tourSteps.indexOf(tourState.currentStep);
    const nextIndex = Math.min(currentIndex + 1, tourSteps.length - 1);
    const nextStep = tourSteps[nextIndex];
    
    
    // Redux'u direkt doğru değerle güncelle (nextTourStep yerine)
    dispatch(forceUpdateTourState({
      currentStep: nextStep,
      stepIndex: nextIndex
    }));
    
    // Route hesapla
    const currentRoute = getStepRoute(tourState.currentStep);
    const nextRoute = getStepRoute(nextStep);
    
    
    if (currentRoute !== nextRoute) {
      // Yeni sayfaya git
      setTimeout(() => {
        navigate(nextRoute);
        
        // Navigation tamamlandığını belirt
        setTimeout(() => {
          dispatch(setNavigationComplete());
        }, 500);
      }, 100);
    } else {
      // Aynı sayfada kal, sadece navigation'ı tamamla
      setTimeout(() => {
        dispatch(setNavigationComplete());
      }, 100);
    }
  };

  const handlePrevTourStep = async () => {
    const tourSteps: TourStep[] = [
      'dashboard', 'employees', 'departments', 'branches',
      'attendance-report', 'shifts', 'leaves', 'tickets', 'forgot-checkout',
      'forgot-checkin', 'assets', 'meals', 'employee-report', 'shift-report',
      'leave-report', 'break-report', 'documents', 'timesheet-report',
      'company-settings', 'notifications', 'tour-button', 'completed'
    ];
    
    const currentIndex = tourSteps.indexOf(tourState.currentStep);
    const prevIndex = Math.max(currentIndex - 1, 0);
    const prevStep = tourSteps[prevIndex];
    

    // Redux'u direkt doğru değerle güncelle
    dispatch(forceUpdateTourState({
      currentStep: prevStep,
      stepIndex: prevIndex
    }));
    
    // Eğer farklı sayfaya gidiyorsak navigate et
    const currentRoute = getStepRoute(tourState.currentStep);
    const prevRoute = getStepRoute(prevStep);
    
    if (currentRoute !== prevRoute) {
      // Önceki sayfaya git
      setTimeout(() => {
        navigate(prevRoute);
        
        // Navigation tamamlandığını belirt
        setTimeout(() => {
          dispatch(setNavigationComplete());
        }, 500);
      }, 100);
    } else {
      // Aynı sayfada kal
      setTimeout(() => {
        dispatch(setNavigationComplete());
      }, 100);
    }
  };

  const handleOpenTour = () => {
    dispatch(openTour());
  };

  const handleCloseTour = () => {
    dispatch(closeTour());
  };

  const handleCompleteTour = () => {
    dispatch(completeTour());
  };

  const handleResetTour = () => {
    dispatch(resetTour());
  };

  const handleSetTourStep = (step: TourStep) => {
    dispatch(setTourStep(step));
    const route = getStepRoute(step);
    navigate(route);
    
    setTimeout(() => {
      dispatch(setNavigationComplete());
    }, 500);
  };

  // Modal tour handlers
  const handleStartModalTour = () => {
    dispatch(startModalTour());
  };

  const handleNextModalStep = () => {
    dispatch(nextModalStep());
  };

  const handlePrevModalStep = () => {
    dispatch(prevModalStep());
  };

  const handleCloseModalTour = () => {
    dispatch(closeModalTour());
  };

  return {
    ...tourState,
    shouldShowTour,
    isAdmin,
    getStepRoute,
    getStepDisplayName,
    startAppTour,
    handleNextTourStep,
    handlePrevTourStep,
    handleOpenTour,
    handleCloseTour,
    handleCompleteTour,
    handleResetTour,
    handleSetTourStep,
    // Modal tour methods
    handleStartModalTour,
    handleNextModalStep,
    handlePrevModalStep,
    handleCloseModalTour,
  };
};